import React, { useState, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { ThemeContext } from '../ThemeContext';

const AdminPanel = () => {
  const { T, isDark } = useContext(ThemeContext);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('ONLINE');

  useEffect(() => {
    // Listen to real-time updates of the analytics document
    const unsub = onSnapshot(doc(db, 'u-jarvis-status', 'analytics'), (docSnap) => {
      if (docSnap.exists()) {
        setTotalUsers(docSnap.data().totalUsers || 1); // fallback to 1 since admin is a user
      }
      setLoading(false);
    }, (error) => {
      console.error("Admin Access Denied", error);
      setStatus('OFFLINE');
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const F = "'Fira Code', monospace";

  const sCard = {
    borderRadius: 14, padding: '24px 30px',
    background: T.cardGrad, border: `1px solid ${T.border}`,
    boxShadow: isDark ? 'none' : '0 2px 16px rgba(0,0,0,0.06)',
  };

  const sHdr = {
    fontSize: 10, letterSpacing: '0.3em', color: T.slate, fontFamily: F,
    marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid ${T.borderHd}`,
    fontWeight: 700, display: 'flex', justifyContent: 'space-between'
  };

  if (loading) {
    return <div style={{ color: T.slate, fontFamily: F, fontSize: 12 }}>DECRYPTING ADMIN VAULT...</div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', width: '100%', fontFamily: F }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: T.rose, letterSpacing: '0.15em', margin: 0 }}>
          ADMIN_COMMAND_CENTER
        </h1>
        <div style={{ fontSize: 9, color: T.slate, letterSpacing: '0.25em', marginTop: 4 }}>
          ELEVATED CLEARANCE LEVEL: MAXIMUM
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        {/* User Analytics Card */}
        <div style={{ ...sCard, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${T.rose}, ${T.violet})` }} />
          <div style={sHdr}>
            <span>👥 PLATFORM DEMOGRAPHICS</span>
            <span style={{ color: T.sky }}>LIVE</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
            <div style={{ fontSize: 54, fontWeight: 900, color: T.text, lineHeight: 1 }}>
              {totalUsers}
            </div>
            <div style={{ fontSize: 10, color: T.slate, letterSpacing: '0.1em', paddingBottom: 8 }}>
              REGISTERED<br />OPERATIVES
            </div>
          </div>
        </div>

        {/* System Health Card */}
        <div style={{ ...sCard, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${T.emerald}, ${T.sky})` }} />
          <div style={sHdr}>
            <span>🛡️ SYSTEM INTEGRITY</span>
            <span style={{ color: status === 'ONLINE' ? T.emerald : T.rose }}>{status}</span>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
              <span style={{ color: T.slate, letterSpacing: '0.1em' }}>DATABASE NODE</span>
              <span style={{ color: T.emerald, fontWeight: 700 }}>SECURE</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
              <span style={{ color: T.slate, letterSpacing: '0.1em' }}>AUTH PROTOCOL</span>
              <span style={{ color: T.emerald, fontWeight: 700 }}>ACTIVE</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
              <span style={{ color: T.slate, letterSpacing: '0.1em' }}>MULTI-USER DOMAIN</span>
              <span style={{ color: T.emerald, fontWeight: 700 }}>VERIFIED</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mock Security Log */}
      <div style={{ ...sCard, marginTop: 16 }}>
        <div style={sHdr}>
          <span>⚠️ SECURITY EVENT LOG</span>
        </div>
        <div style={{ fontSize: 10, color: T.slate, lineHeight: 1.8, background: 'rgba(0,0,0,0.2)', padding: 14, borderRadius: 8 }}>
          <div style={{ color: T.rose }}>[SYSTEM] root@admin access granted from secure local port.</div>
          <div style={{ color: T.emerald }}>[AUTH] Firebase Auth multi-tenant architecture deployed safely.</div>
          <div style={{ color: T.sky }}>[ROUTING] No-index meta tags active to prevent scanner bots.</div>
          <div style={{ color: T.amber }}>[LOGS] End of recent security events.</div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
